import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ChevronDown, ChevronRight, Copy, Check, Eye, EyeOff, Plus, X } from 'lucide-react'
import type { Device, AdvancedConfig, CameraConfig } from '@/types'

interface DeviceConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  device: Device
  onDeviceUpdated: () => void
}

export function DeviceConfigDialog({ open, onOpenChange, device, onDeviceUpdated }: DeviceConfigDialogProps) {
  const { t } = useTranslation()

  // Form state
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [cameras, setCameras] = useState<CameraConfig[]>([])
  const [advancedConfig, setAdvancedConfig] = useState<AdvancedConfig>({
    segmentsBefore: 1,
    segmentsAfter: 1
  })

  // UI state
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedId, setCopiedId] = useState(false)

  // Initialize form from device
  useEffect(() => {
    if (device) {
      setName(device.name)
      setLocation(device.location || '')
      setCameras(device.cameras || [])
      setAdvancedConfig(device.advancedConfig || {
        segmentsBefore: 1,
        segmentsAfter: 1
      })
    }
  }, [device])

  const handleSave = async () => {
    setError('')

    if (!name.trim()) {
      setError(t('deviceConfig.deviceNameRequired'))
      return
    }

    try {
      setSaving(true)
      const updateDevice = apiCallable('updateDevice')
      const result = await updateDevice({
        id: device.id,
        name: name.trim(),
        location: location.trim() || null,
        cameras: cameras.filter(c => c.url.trim()),
        advancedConfig
      })

      const data = result.data as { success: boolean; message?: string }

      if (data.success) {
        onDeviceUpdated()
        onOpenChange(false)
      } else {
        setError(data.message || t('deviceConfig.failedToUpdate'))
      }
    } catch (err) {
      console.error('Update device error:', err)
      setError(t('deviceConfig.failedToUpdate'))
    } finally {
      setSaving(false)
    }
  }

  const handleCopyApiKey = async () => {
    if (device.apiKey) {
      await navigator.clipboard.writeText(device.apiKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleCopyId = async () => {
    await navigator.clipboard.writeText(device.id)
    setCopiedId(true)
    setTimeout(() => setCopiedId(false), 2000)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('deviceConfig.title', { name: device.name })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">{t('deviceConfig.basicInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('deviceConfig.deviceName')}</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">{t('deviceConfig.location')}</Label>
                  <Input
                    id="location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>
              </div>

              {/* Device ID (read-only, copyable — used for CLI/SSH tooling) */}
              <div className="space-y-2">
                <Label>{t('deviceConfig.deviceId')}</Label>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 min-w-0 truncate rounded-md bg-muted px-3 py-2 font-mono text-sm"
                    title={device.id}
                  >
                    {device.id}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyId}
                    title={t('deviceConfig.copyDeviceId')}
                    aria-label={t('deviceConfig.copyDeviceId')}
                  >
                    {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cameras */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">{t('deviceConfig.cameras')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cameras.map((camera, index) => (
                <div key={camera.id} className="flex gap-2 items-start">
                  <div className="space-y-1 w-1/4">
                    {index === 0 && <Label className="text-xs">{t('deviceConfig.cameraName')}</Label>}
                    <Input
                      value={camera.name}
                      onChange={(e) => {
                        const updated = [...cameras]
                        updated[index] = { ...updated[index], name: e.target.value }
                        setCameras(updated)
                      }}
                      placeholder={`Camera ${index + 1}`}
                    />
                  </div>
                  <div className="space-y-1 flex-1">
                    {index === 0 && <Label className="text-xs">{t('deviceConfig.rtspUrl')}</Label>}
                    <Input
                      value={camera.url}
                      onChange={(e) => {
                        const updated = [...cameras]
                        updated[index] = { ...updated[index], url: e.target.value }
                        setCameras(updated)
                      }}
                      placeholder="rtsp://camera.local/stream"
                    />
                  </div>
                  <div className="space-y-1 flex-shrink-0">
                    {index === 0 && <Label className="text-xs">{t('deviceConfig.detectOnly')}</Label>}
                    <div className={index === 0 ? 'pt-0.5' : 'pt-2'}>
                      <Switch
                        checked={camera.detectOnly || false}
                        onCheckedChange={(checked) => {
                          const updated = [...cameras]
                          updated[index] = { ...updated[index], detectOnly: checked }
                          setCameras(updated)
                        }}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={index === 0 ? 'mt-5' : ''}
                    onClick={() => setCameras(cameras.filter((_, i) => i !== index))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCameras([...cameras, { id: crypto.randomUUID(), name: `Camera ${cameras.length + 1}`, url: '', detectOnly: false }])}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('deviceConfig.addCamera')}
              </Button>
            </CardContent>
          </Card>

          {/* Advanced Config */}
          <Card>
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="py-4">
                  <div className="flex items-center gap-2">
                    {advancedOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <CardTitle className="text-base">{t('deviceConfig.advanced')}</CardTitle>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="segments-before">{t('deviceConfig.segmentsBefore')}</Label>
                      <Input
                        id="segments-before"
                        type="number"
                        min={0}
                        max={5}
                        value={advancedConfig.segmentsBefore}
                        onChange={(e) =>
                          setAdvancedConfig({
                            ...advancedConfig,
                            segmentsBefore: parseInt(e.target.value) || 0
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="segments-after">{t('deviceConfig.segmentsAfter')}</Label>
                      <Input
                        id="segments-after"
                        type="number"
                        min={0}
                        max={5}
                        value={advancedConfig.segmentsAfter}
                        onChange={(e) =>
                          setAdvancedConfig({
                            ...advancedConfig,
                            segmentsAfter: parseInt(e.target.value) || 0
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Face Recognition Overrides */}
                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-medium mb-3">{t('deviceConfig.faceRecognition')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="face-similarity-threshold">{t('deviceConfig.similarityThreshold')}</Label>
                        <Input
                          id="face-similarity-threshold"
                          type="number"
                          min={0}
                          max={1}
                          step={0.01}
                          placeholder="Group default: 0.45"
                          value={advancedConfig.faceSimilarityThreshold ?? ''}
                          onChange={(e) =>
                            setAdvancedConfig({
                              ...advancedConfig,
                              faceSimilarityThreshold: e.target.value === '' ? undefined : parseFloat(e.target.value)
                            })
                          }
                        />
                        <p className="text-xs text-muted-foreground">{t('deviceConfig.similarityThresholdHint')}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="face-merge-threshold">{t('deviceConfig.autoMergeThreshold')}</Label>
                        <Input
                          id="face-merge-threshold"
                          type="number"
                          min={0}
                          max={1}
                          step={0.01}
                          placeholder="Group default: 0.35"
                          value={advancedConfig.faceUnknownMergeThreshold ?? ''}
                          onChange={(e) =>
                            setAdvancedConfig({
                              ...advancedConfig,
                              faceUnknownMergeThreshold: e.target.value === '' ? undefined : parseFloat(e.target.value)
                            })
                          }
                        />
                        <p className="text-xs text-muted-foreground">{t('deviceConfig.autoMergeThresholdHint')}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="face-merge-window">{t('deviceConfig.autoMergeWindow')}</Label>
                        <Input
                          id="face-merge-window"
                          type="number"
                          min={1}
                          max={120}
                          step={1}
                          placeholder="Group default: 30"
                          value={advancedConfig.faceUnknownMergeWindowMinutes ?? ''}
                          onChange={(e) =>
                            setAdvancedConfig({
                              ...advancedConfig,
                              faceUnknownMergeWindowMinutes: e.target.value === '' ? undefined : parseInt(e.target.value)
                            })
                          }
                        />
                        <p className="text-xs text-muted-foreground">{t('deviceConfig.autoMergeWindowHint')}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* API Key - Only shown to group admins */}
          {device.apiKey && (
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base">{t('deviceConfig.apiKey')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={device.apiKey}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyApiKey}
                  >
                    {copied ? <Check className="h-4 w-4 text-gold" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('deviceConfig.saveConfig')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
